import type { Invoice } from '../types';

function sanitizePdfText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E\n\r\t]/g, ' ')
    .replace(/[()\\]/g, (match) => `\\${match}`)
    .trim();
}

function wrapPdfText(text: string, maxLength: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function money(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);
}

function date(value: string): string {
  return new Intl.DateTimeFormat('en-GB').format(new Date(value));
}

export function buildInvoicePdf(invoice: Invoice): Blob {
  const rawLines = [
    `Invoice ${invoice.invoiceNumber}`,
    'FUCHSIA HOME CARE CATALIN',
    'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    'Tel: 01493738488 | On Call: 07443145811 | Email: lowestoft@fuchsiahomecare.co.uk',
    '',
    `Payer: ${invoice.payerName}`,
    `Client: ${invoice.clientName || 'N/A'}`,
    `Status: ${invoice.status.replace(/_/g, ' ')}`,
    `Invoice date: ${date(invoice.invoiceDate)} | Due date: ${date(invoice.dueDate)}`,
    `Period: ${date(invoice.periodStart)} - ${date(invoice.periodEnd)}`,
    '',
    'LINE ITEMS',
    ...invoice.lineItems.flatMap((item) =>
      wrapPdfText(
        `${date(item.serviceDate)} | ${item.serviceDescription} | ${item.units} ${item.unitType.toLowerCase()} x ${money(item.unitRate)} = ${money(item.total)}`,
        92
      )
    ),
    '',
    `Subtotal: ${money(invoice.subtotal)}`,
    `Tax: ${money(invoice.taxAmount)}`,
    `Discount: ${money(invoice.discountAmount)}`,
    `Adjustments: ${money(invoice.adjustmentAmount)}`,
    `Total: ${money(invoice.totalAmount)}`,
    `Paid: ${money(invoice.paidAmount)}`,
    `Balance due: ${money(invoice.balanceDue)}`,
    '',
    invoice.notes ? `Notes: ${invoice.notes}` : '',
    'Generated by Fuchsia Home Care billing and invoicing module.',
  ].filter(Boolean).map(sanitizePdfText);

  const linesPerPage = 56;
  const pages: string[][] = [];
  for (let index = 0; index < rawLines.length; index += linesPerPage) {
    pages.push(rawLines.slice(index, index + linesPerPage));
  }

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const catalogId = addObject('<< /Type /Catalog /Pages 2 0 R >>');
  const pagesId = addObject('');
  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pageIds: number[] = [];

  pages.forEach((pageLines) => {
    const content = [
      'BT',
      '/F1 10 Tf',
      '48 790 Td',
      '14 TL',
      ...pageLines.map((line, lineIndex) =>
        lineIndex === 0 ? `/F1 16 Tf (${line}) Tj /F1 10 Tf T*` : `(${line}) Tj T*`
      ),
      'ET',
    ].join('\n');
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

  const chunks = ['%PDF-1.4\n'];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(chunks.join('').length);
    chunks.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  const xrefOffset = chunks.join('').length;
  chunks.push(`xref\n0 ${objects.length + 1}\n`);
  chunks.push('0000000000 65535 f \n');
  offsets.slice(1).forEach((offset) => {
    chunks.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  chunks.push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}

export function downloadInvoicePdf(invoice: Invoice): void {
  const url = URL.createObjectURL(buildInvoicePdf(invoice));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoiceNumber.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function printInvoicePdf(invoice: Invoice): boolean {
  const url = URL.createObjectURL(buildInvoicePdf(invoice));
  const pdfWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!pdfWindow) {
    URL.revokeObjectURL(url);
    return false;
  }
  window.setTimeout(() => {
    pdfWindow.print();
    URL.revokeObjectURL(url);
  }, 700);
  return true;
}
